<template>
  <div class="cp">
    <div v-if="loading" class="cm-loading" style="padding: 24px;"><div class="cm-spinner"></div> Loading…</div>
    <div v-else-if="error" class="cm-empty" style="padding: 24px;"><div class="msg">{{ error }}</div></div>

    <div v-else class="cp-cols">
      <section class="cp-col cp-list">
        <select v-model="typeId" class="cm-select cm-select-sm" @change="onTypeChange">
          <option v-for="t in entityTypes" :key="t.id" :value="t.id">{{ t.type_name }}</option>
        </select>
        <input v-model.trim="search" class="cm-input cm-input-sm" placeholder="Find by name" />
        <ul class="cp-rows">
          <li
            v-for="e in visibleEntities"
            :key="e.id"
            :class="{ on: selectedId === e.id, bare: memberMode && activeCards(e.id).length === 0 }"
            @click="select(e.id)"
          >
            <div class="cp-row-top">
              <span class="cp-row-name">{{ e.name }}</span>
              <span v-if="memberMode && caseloadFor(e.id)" class="cp-row-count cm-tnum">{{ caseloadFor(e.id).total }}</span>
            </div>
            <div class="cp-row-sub">
              <span v-if="memberMode && isPaused(e.id)" class="cp-away-dot">not taking new patients</span>
              <span v-else>{{ summaryLine(e.id) }}</span>
            </div>
          </li>
          <li v-if="entitiesLoading" class="cp-none">Loading…</li>
          <li v-else-if="visibleEntities.length === 0" class="cp-none">Nothing matches.</li>
        </ul>
        <footer class="cp-foot"><span>{{ entities.length }} {{ typeName }}</span></footer>
      </section>

      <section v-if="selected && memberMode" class="cp-col cp-detail">
        <header class="cp-head">
          <div>
            <h2>{{ selected.name }}</h2>
            <div class="cp-sub">{{ roleFor(selected.id) || typeName }}</div>
          </div>
          <label class="cp-switch" :class="{ busy: busy }">
            <input type="checkbox" :checked="!isPaused(selected.id)" :disabled="busy || coverageFor(selected.id).length === 0" @change="setTakingPatients(selected.id, $event.target.checked)" />
            <span>Takes new patients</span>
          </label>
        </header>

        <div v-if="notice" class="cp-banner" :class="{ bad: notice.error }">
          <div>{{ notice.text }}</div>
          <button class="cp-x" @click="notice = null">×</button>
        </div>

        <div v-if="away[selected.id]" class="cp-banner">
          <div>
            <strong>Away.</strong> Not taking new patients.
            <template v-if="away[selected.id].backups.length">
              Covered by
              <template v-for="(b, i) in away[selected.id].backups" :key="b.memberId">{{ i ? ', ' : '' }}{{ b.name }}<template v-if="b.programs.length"> for {{ b.programs.join(', ') }}</template></template>.
            </template>
          </div>
          <button class="cm-btn cm-btn-secondary cm-btn-sm" :disabled="busy" @click="comeBack(selected.id)">Back now</button>
        </div>

        <section class="cp-block">
          <header class="cp-block-head">
            <h3>Caseload</h3>
            <div class="cp-block-actions">
              <button class="cm-btn cm-btn-secondary cm-btn-sm" :disabled="busy || !!away[selected.id]" @click="openForm('away')">Mark away</button>
              <button class="cm-btn cm-btn-secondary cm-btn-sm" :disabled="busy" @click="openForm('handover')">Hand over</button>
            </div>
          </header>
          <table v-if="caseloadFor(selected.id)" class="cm-table cp-table">
            <thead><tr><th>Program</th><th class="num">Open tasks</th></tr></thead>
            <tbody>
              <tr v-for="p in caseloadFor(selected.id).programs" :key="p.metric_id">
                <td>{{ p.metric_name }}</td>
                <td class="num cm-tnum">{{ p.count }}</td>
              </tr>
              <tr class="cp-total">
                <td>All programs <span class="cp-sub">{{ caseloadFor(selected.id).overdue }} overdue, {{ caseloadFor(selected.id).snoozed }} snoozed</span></td>
                <td class="num cm-tnum">{{ caseloadFor(selected.id).total }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="cp-none">No open work assigned.</p>

          <div v-if="form && form.kind === 'away'" class="cp-form">
            <h4>Mark {{ firstName(selected.name) }} away</h4>
            <p class="cp-hint">Stops new patients going to {{ firstName(selected.name) }} and gives each backup a copy of their coverage. Press Back now when they return.</p>
            <div class="cp-form-field">
              <span>Who covers, and for which programs</span>
              <div v-for="b in form.backups" :key="b.memberId" class="cp-backup">
                <span class="cp-chip person">{{ b.name }}</span>
                <span v-for="p in b.programs" :key="p" class="cp-chip">{{ p }}<button class="cp-x" @click="toggle(b.programs, p)">×</button></span>
                <span v-if="b.programs.length === 0" class="cp-all">all programs</span>
                <select class="cm-select cm-select-sm" @change="addProgramTo(b, $event)">
                  <option value="">Limit to a program…</option>
                  <option v-for="p in programsNotIn(b.programs)" :key="p" :value="p">{{ p }}</option>
                </select>
                <button class="cp-x" title="Remove" @click="removeBy(form.backups, 'memberId', b.memberId)">×</button>
              </div>
              <input v-model.trim="q.person" class="cm-input cm-input-sm" placeholder="Add a backup" />
              <ul v-if="q.person" class="cp-picker">
                <li v-for="p in peopleMatching(q.person, form.backups.map(b => b.memberId).concat(selected.id))" :key="p.id" @click="addBackup(p)">{{ p.name }} <em>{{ p.role || '' }}</em></li>
              </ul>
            </div>
            <label class="cp-radio"><input v-model="form.moveTasks" type="checkbox" /> Also move {{ firstName(selected.name) }}'s open tasks to the backups as cover. Ownership stays, so work returns when they're back.</label>
            <div class="cp-form-foot">
              <button class="cm-btn cm-btn-secondary cm-btn-sm" @click="form = null">Cancel</button>
              <button class="cm-btn cm-btn-primary cm-btn-sm" :disabled="busy" @click="applyAway">Mark away</button>
            </div>
          </div>

          <div v-if="form && form.kind === 'handover'" class="cp-form">
            <h4>Hand over {{ firstName(selected.name) }}'s patients</h4>
            <div class="cp-form-field">
              <span>To</span>
              <div v-if="form.to" class="cp-chips"><span class="cp-chip person">{{ form.to.name }}<button class="cp-x" @click="form.to = null">×</button></span></div>
              <input v-else v-model.trim="q.person" class="cm-input cm-input-sm" placeholder="Find a person" />
              <ul v-if="!form.to && q.person" class="cp-picker">
                <li v-for="p in peopleMatching(q.person, [selected.id])" :key="p.id" @click="form.to = p; q.person = ''">{{ p.name }} <em>{{ p.role || '' }}</em></li>
              </ul>
            </div>
            <div class="cp-form-field">
              <span>Which patients</span>
              <div class="cp-chips">
                <span v-for="p in form.programs" :key="p" class="cp-chip">{{ p }}<button class="cp-x" @click="toggle(form.programs, p)">×</button></span>
                <span v-if="form.programs.length === 0" class="cp-all">Every program</span>
              </div>
              <select class="cm-select cm-select-sm" @change="addProgramTo(form, $event)">
                <option value="">Limit to a program…</option>
                <option v-for="p in programsNotIn(form.programs)" :key="p" :value="p">{{ p }}</option>
              </select>
            </div>
            <div class="cp-form-field">
              <span>How</span>
              <label class="cp-radio"><input v-model="form.mode" type="radio" value="transfer" /> Transfer ownership. Future stages follow {{ form.to ? form.to.name : 'the new owner' }}.</label>
              <label class="cp-radio"><input v-model="form.mode" type="radio" value="cover" /> Cover the current tasks only. Ownership stays with {{ firstName(selected.name) }}.</label>
            </div>
            <div class="cp-form-foot">
              <button class="cm-btn cm-btn-secondary cm-btn-sm" @click="form = null">Cancel</button>
              <button class="cm-btn cm-btn-primary cm-btn-sm" :disabled="!form.to || busy" @click="applyHandover">{{ form.mode === 'transfer' ? 'Transfer' : 'Cover' }} {{ handoverCount }} tasks</button>
            </div>
          </div>
        </section>

        <section class="cp-block">
          <header class="cp-block-head">
            <h3>Coverage</h3>
            <div class="cp-block-actions">
              <label class="cp-toggle"><input v-model="showEnded" type="checkbox" /> Show ended</label>
              <button class="cm-btn cm-btn-primary cm-btn-sm" :disabled="busy" @click="addCoverage">Add coverage</button>
            </div>
          </header>

          <article v-for="r in visibleCoverage" :key="r.key" class="cp-card" :class="{ ended: !r.active }">
            <header class="cp-card-head">
              <div class="cp-sub"><template v-if="!r.active">Ended · </template><template v-if="r.since">since {{ r.since }}</template></div>
              <div class="cp-block-actions">
                <template v-if="editingKey === r.key">
                  <button class="cm-btn cm-btn-secondary cm-btn-sm" :disabled="busy" @click="cancelEdit(r)">Cancel</button>
                  <button class="cm-btn cm-btn-primary cm-btn-sm" :disabled="busy" @click="saveCard(r)">Save</button>
                </template>
                <template v-else>
                  <button v-if="r.active" class="cm-btn cm-btn-secondary cm-btn-sm" :disabled="busy" @click="startEdit(r)">Edit</button>
                  <button v-if="r.active" class="cm-btn cm-btn-secondary cm-btn-sm" :disabled="busy" @click="endCard(r)">End</button>
                  <button v-else class="cm-btn cm-btn-secondary cm-btn-sm" :disabled="busy" @click="restoreCard(r)">Restore</button>
                </template>
              </div>
            </header>

            <div v-if="editingKey !== r.key" class="cp-card-body">
              <div class="cp-facet">
                <h5>Where</h5>
                <p v-if="r.practices.length === 0" class="cp-all">Every practice</p>
                <div v-for="g in groupPractices(r.practices)" :key="g.type" class="cp-group">
                  <div class="cp-group-title">{{ g.type }} <span class="cm-tnum">{{ g.items.length }}</span></div>
                  <div class="cp-chips">
                    <span v-for="e in (expanded[r.key + g.type] ? g.items : g.items.slice(0, 8))" :key="e.id" class="cp-tag">{{ e.name }}</span>
                    <button v-if="g.items.length > 8" class="cp-more" @click="expanded[r.key + g.type] = !expanded[r.key + g.type]">{{ expanded[r.key + g.type] ? 'Show fewer' : `${g.items.length - 8} more` }}</button>
                  </div>
                </div>
              </div>
              <div class="cp-facet">
                <h5>Programs and stages</h5>
                <p v-if="r.programs.length === 0" class="cp-all">Every program, every stage</p>
                <ul class="cp-programs">
                  <li v-for="p in r.programs" :key="p"><span class="cp-program">{{ p }}</span><span class="cp-stages">{{ stagesInProgram(r, p) }}</span></li>
                </ul>
              </div>
              <div class="cp-facet">
                <h5>Contracts</h5>
                <p v-if="r.contracts.length === 0" class="cp-all">Every contract</p>
                <div v-else class="cp-chips"><span v-for="c in r.contracts" :key="c.id" class="cp-tag">{{ c.name }}</span></div>
              </div>
            </div>

            <div v-else class="cp-fields">
              <div class="cp-form-field">
                <span>Practices</span>
                <div class="cp-chips">
                  <span v-for="e in r.practices" :key="e.id" class="cp-chip" :title="e.type">{{ e.name }}<button class="cp-x" @click="removeBy(r.practices, 'id', e.id)">×</button></span>
                  <span v-if="r.practices.length === 0" class="cp-all">Every practice</span>
                </div>
                <input v-model.trim="q.practice" class="cm-input cm-input-sm" placeholder="Add a department, service area or TIN" />
                <ul v-if="q.practice" class="cp-picker">
                  <li v-for="e in matchesById(practiceOptions, r.practices, q.practice)" :key="e.id" @click="addObj(r.practices, e, 'practice')">{{ e.name }} <em>{{ e.type }}</em></li>
                </ul>
              </div>
              <div class="cp-form-field">
                <span>Programs</span>
                <div class="cp-chips">
                  <span v-for="p in r.programs" :key="p" class="cp-chip">{{ p }}<button class="cp-x" @click="toggle(r.programs, p)">×</button></span>
                  <span v-if="r.programs.length === 0" class="cp-all">Every program</span>
                </div>
                <input v-model.trim="q.program" class="cm-input cm-input-sm" placeholder="Add a program" />
                <ul v-if="q.program" class="cp-picker">
                  <li v-for="p in matches(programNames, r.programs, q.program)" :key="p" @click="add(r.programs, p, 'program')">{{ p }}</li>
                </ul>
              </div>
              <div class="cp-form-field">
                <span>Stages</span>
                <div class="cp-chips">
                  <span v-for="st in r.stages" :key="st" class="cp-chip">{{ st }}<button class="cp-x" @click="toggle(r.stages, st)">×</button></span>
                  <span v-if="r.stages.length === 0" class="cp-all">Every stage</span>
                </div>
                <input v-model.trim="q.stage" class="cm-input cm-input-sm" placeholder="Add a stage" />
                <ul v-if="q.stage" class="cp-picker">
                  <li v-for="st in matches(stagesFor(r.programs), r.stages, q.stage)" :key="st" @click="add(r.stages, st, 'stage')">{{ st }}</li>
                </ul>
              </div>
              <div class="cp-form-field">
                <span>Contracts</span>
                <div class="cp-chips">
                  <span v-for="c in r.contracts" :key="c.id" class="cp-chip">{{ c.name }}<button class="cp-x" @click="removeBy(r.contracts, 'id', c.id)">×</button></span>
                  <span v-if="r.contracts.length === 0" class="cp-all">Every contract</span>
                </div>
                <input v-model.trim="q.contract" class="cm-input cm-input-sm" placeholder="Add a contract" />
                <ul v-if="q.contract" class="cp-picker">
                  <li v-for="c in matchesById(contractOptions, r.contracts, q.contract)" :key="c.id" @click="addObj(r.contracts, c, 'contract')">{{ c.name }}</li>
                </ul>
              </div>
            </div>
          </article>
          <p v-if="visibleCoverage.length === 0" class="cp-none">{{ selected.name }} has no coverage. They receive no work automatically.</p>
        </section>

        <section class="cp-block">
          <header class="cp-block-head"><h3>Done this session</h3></header>
          <ul class="cp-history">
            <li v-for="h in historyFor(selected.id)" :key="h.key"><span class="cm-tnum">{{ h.at }}</span> {{ h.text }}</li>
            <li v-if="historyFor(selected.id).length === 0" class="cp-none">Nothing yet.</li>
          </ul>
        </section>
      </section>

      <section v-else-if="selected" class="cp-col cp-detail">
        <header class="cp-head"><div><h2>{{ selected.name }}</h2><div class="cp-sub">{{ typeName }}</div></div></header>
        <section class="cp-block">
          <header class="cp-block-head"><h3>Who covers this</h3></header>
          <table class="cm-table cp-table">
            <thead><tr><th>Person</th><th>Programs</th><th>Stages</th><th>Contracts</th><th>Since</th></tr></thead>
            <tbody>
              <tr v-for="r in practiceRowsFor(selected.id)" :key="r.key">
                <td class="strong">{{ r.person.name }}<div class="cp-role">{{ r.person.role || '' }}</div></td>
                <td>{{ r.programs.length ? r.programs.join(', ') : 'Every program' }}</td>
                <td>{{ r.stages.length ? r.stages.join(', ') : 'Every stage' }}</td>
                <td>{{ r.contracts.length ? summarize(r.contracts.map(c => c.name), 2) : 'Every contract' }}</td>
                <td class="cm-tnum">{{ r.since }}</td>
              </tr>
              <tr v-if="practiceRowsFor(selected.id).length === 0"><td colspan="5" class="cp-none">Nobody covers {{ selected.name }} directly. Work for it goes unassigned unless a wider rule catches it.</td></tr>
            </tbody>
          </table>
        </section>
      </section>

      <section v-else class="cp-col cp-blank"><p>Pick someone on the left.</p></section>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { listCareManagementPrograms } from '@/services/api/careManagementPrograms';
import { fetchPatientWorkQueue } from '@/services/api/patientWorkQueue';

const MEMBER_TYPE_NAMES = ['CM Staff', 'CM Manager'];
const ACTIVE_TASK_STATUSES = [1, 4, 7];

export default {
  name: 'PilotAssignments',
  data() {
    return {
      loading: true,
      entitiesLoading: false,
      busy: false,
      error: null,
      notice: null,
      rules: [],
      programNames: [],
      programIdByName: {},
      stageIdByName: {},
      stagesByProgram: {},
      entityTypes: [],
      typeId: null,
      entities: [],
      caseloads: {},
      coverageByMember: {},
      practiceRows: {},
      away: {},
      history: [],
      selectedId: null,
      editingKey: null,
      editBackup: null,
      form: null,
      search: '',
      q: { practice: '', program: '', stage: '', contract: '', person: '' },
      showEnded: false,
      expanded: {},
      nextKey: 1,
    };
  },
  computed: {
    typeName() {
      return this.entityTypes.find(t => t.id === this.typeId)?.type_name || '';
    },
    memberMode() {
      return MEMBER_TYPE_NAMES.includes(this.typeName);
    },
    selected() {
      return this.entities.find(e => e.id === this.selectedId) || null;
    },
    metricIdsParam() {
      return Object.values(this.programIdByName).join(',');
    },
    visibleEntities() {
      const s = this.search.toLowerCase();
      return this.entities.filter(e => !s || e.name.toLowerCase().includes(s));
    },
    visibleCoverage() {
      const rows = this.coverageFor(this.selectedId).filter(r => this.showEnded || r.active || this.editingKey === r.key);
      const weight = r => (r.practices.length || 999) * Math.max(1, r.programs.length || 999);
      return [...rows].sort((a, b) => (a.active ? 0 : 1) - (b.active ? 0 : 1) || weight(b) - weight(a));
    },
    practiceOptions() {
      const seen = {};
      for (const r of this.rules) if (r.entity && !seen[r.entity]) seen[r.entity] = { id: r.entity, name: r.entity_name, type: r.entity_type_name };
      return Object.values(seen).sort((a, b) => a.name.localeCompare(b.name));
    },
    contractOptions() {
      const seen = {};
      for (const r of this.rules) if (r.contract && !seen[r.contract]) seen[r.contract] = { id: r.contract, name: r.contract_name };
      return Object.values(seen).sort((a, b) => a.name.localeCompare(b.name));
    },
    people() {
      const seen = {};
      for (const r of this.rules) if (r.member && !seen[r.member]) seen[r.member] = { id: r.member, name: r.member_name, role: r.role_name, roleId: r.role };
      for (const e of this.memberMode ? this.entities : []) if (!seen[e.id]) seen[e.id] = { id: e.id, name: e.name, role: null, roleId: null };
      return Object.values(seen).sort((a, b) => a.name.localeCompare(b.name));
    },
    handoverCount() {
      const cl = this.caseloadFor(this.selectedId);
      if (!cl || !this.form) return 0;
      if (!this.form.programs.length) return cl.total;
      return cl.programs.filter(p => this.form.programs.includes(p.metric_name)).reduce((n, p) => n + p.count, 0);
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    today() {
      return new Date().toISOString().slice(0, 10);
    },
    firstName(name) {
      return (name || '').split(' ')[0];
    },
    coverageFor(memberId) {
      return this.coverageByMember[memberId] || [];
    },
    activeCards(memberId) {
      return this.coverageFor(memberId).filter(r => r.active);
    },
    isPaused(memberId) {
      return this.coverageFor(memberId).length > 0 && this.activeCards(memberId).length === 0;
    },
    practiceRowsFor(entityId) {
      return this.practiceRows[entityId] || [];
    },
    caseloadFor(memberId) {
      return this.caseloads[memberId] || null;
    },
    roleFor(memberId) {
      return this.caseloadFor(memberId)?.role || this.people.find(p => p.id === memberId)?.role || '';
    },
    roleIdFor(memberId) {
      const card = this.coverageFor(memberId).find(r => r.roleId);
      return card ? card.roleId : (this.people.find(p => p.id === memberId)?.roleId || null);
    },
    historyFor(memberId) {
      return this.history.filter(h => h.memberId === memberId);
    },
    log(memberId, text) {
      this.history.unshift({ key: this.nextKey++, memberId, at: new Date().toTimeString().slice(0, 5), text });
    },
    summaryLine(entityId) {
      if (this.memberMode) {
        const rows = this.activeCards(entityId);
        if (!rows.length) return 'no coverage';
        const practices = new Set(); let every = false; const programs = new Set();
        for (const r of rows) { if (!r.practices.length) every = true; r.practices.forEach(p => practices.add(p.id)); r.programs.forEach(p => programs.add(p)); }
        return `${every ? 'every practice' : `${practices.size} practice${practices.size === 1 ? '' : 's'}`} · ${programs.size ? `${programs.size} program${programs.size === 1 ? '' : 's'}` : 'every program'}`;
      }
      const names = [...new Set(this.practiceRowsFor(entityId).map(r => r.person.name.split(' ')[0]))];
      if (!names.length) return 'nobody covers this';
      return names.length <= 3 ? names.join(', ') : `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
    },
    summarize(names, n) {
      return names.length <= n ? names.join(', ') : `${names.slice(0, n).join(', ')} +${names.length - n}`;
    },
    groupPractices(practices) {
      const groups = {};
      for (const e of practices) (groups[e.type] || (groups[e.type] = [])).push(e);
      const order = ['Service Area', 'TIN', 'Practice', 'Department'];
      return Object.entries(groups)
        .map(([type, items]) => ({ type: `${type}${items.length === 1 ? '' : 's'}`, raw: type, items: [...items].sort((a, b) => a.name.localeCompare(b.name)) }))
        .sort((a, b) => ((order.indexOf(a.raw) + 99) % 100) - ((order.indexOf(b.raw) + 99) % 100));
    },
    stagesInProgram(row, program) {
      const all = this.stagesByProgram[program] || [];
      if (row.stages.length === 0) return 'every stage';
      const chosen = all.filter(st => row.stages.includes(st));
      if (chosen.length === 0) return 'no stage selected';
      if (chosen.length === all.length) return 'every stage';
      return chosen.join(', ');
    },
    stagesFor(programs) {
      const out = new Set();
      for (const p of programs.length ? programs : this.programNames) for (const st of this.stagesByProgram[p] || []) out.add(st);
      return [...out].sort();
    },
    matches(options, chosen, query) {
      const s = query.toLowerCase();
      return options.filter(o => !chosen.includes(o) && o.toLowerCase().includes(s)).slice(0, 10);
    },
    matchesById(options, chosen, query) {
      const s = query.toLowerCase();
      const ids = new Set(chosen.map(c => c.id));
      return options.filter(o => !ids.has(o.id) && o.name.toLowerCase().includes(s)).slice(0, 10);
    },
    peopleMatching(query, excludeIds) {
      const s = query.toLowerCase();
      const ex = new Set(excludeIds);
      return this.people.filter(p => !ex.has(p.id) && p.name.toLowerCase().includes(s)).slice(0, 10);
    },
    programsNotIn(list) {
      return this.programNames.filter(p => !list.includes(p));
    },
    addProgramTo(target, event) {
      const value = event.target.value;
      if (value) target.programs.push(value);
      event.target.value = '';
    },
    addBackup(p) {
      this.form.backups.push({ memberId: p.id, name: p.name, programs: [] });
      this.q.person = '';
    },
    select(id) {
      this.selectedId = id;
      this.editingKey = null;
      this.editBackup = null;
      this.form = null;
      this.notice = null;
      this.q = { practice: '', program: '', stage: '', contract: '', person: '' };
    },
    toggle(list, value) {
      const i = list.indexOf(value);
      if (i >= 0) list.splice(i, 1); else list.push(value);
    },
    add(list, value, field) {
      list.push(value);
      this.q[field] = '';
    },
    addObj(list, value, field) {
      list.push({ ...value });
      this.q[field] = '';
    },
    removeBy(list, field, value) {
      const i = list.findIndex(x => x[field] === value);
      if (i >= 0) list.splice(i, 1);
    },
    openForm(kind) {
      this.q.person = '';
      this.form = kind === 'away'
        ? { kind, backups: [], moveTasks: true }
        : { kind, to: null, programs: [], mode: 'transfer' };
    },

    tupleKeys(card) {
      const ids = (list, map) => (list.length ? list.map(x => (map ? map[x] : x.id)).filter(Boolean) : [null]);
      const out = new Set();
      for (const e of ids(card.practices)) for (const c of ids(card.contracts)) for (const m of ids(card.programs, this.programIdByName)) for (const t of ids(card.stages, this.stageIdByName)) {
        out.add(`${e || 0}|${c || 0}|${m || 0}|${t || 0}`);
      }
      return out;
    },
    ruleKey(r) {
      return `${r.entity || 0}|${r.contract || 0}|${r.metric || 0}|${r.task_type || 0}`;
    },
    cardPayload(memberId, card, programs) {
      const payload = { member_id: memberId, replace: false, role_id: card.roleId || this.roleIdFor(memberId) };
      if (card.practices.length) payload.entity_ids = card.practices.map(p => p.id);
      if (card.contracts.length) payload.contract_ids = card.contracts.map(c => c.id);
      const programList = programs || card.programs;
      if (programList.length) payload.metric_ids = programList.map(p => this.programIdByName[p]).filter(Boolean);
      if (card.stages.length) payload.task_type_ids = card.stages.map(st => this.stageIdByName[st]).filter(Boolean);
      return payload;
    },
    async setActive(ids, isActive) {
      if (!ids.length) return 0;
      const { data } = await axios.post('/api/team-assignments/bulk-set-active/', { ids, is_active: isActive });
      return data.updated;
    },
    async run(memberId, label, fn) {
      this.busy = true;
      this.notice = null;
      try {
        const text = await fn();
        await this.reloadRules();
        this.log(memberId, text || label);
      } catch (e) {
        this.notice = { error: true, text: `${label} failed: ${e?.response?.data?.error || e?.response?.data?.message || e?.response?.data?.detail || e.message}` };
      } finally {
        this.busy = false;
      }
    },

    startEdit(card) {
      this.editBackup = JSON.stringify({ practices: card.practices, programs: card.programs, stages: card.stages, contracts: card.contracts });
      this.editingKey = card.key;
    },
    cancelEdit(card) {
      if (card.isNew) this.removeBy(this.coverageFor(this.selectedId), 'key', card.key);
      else Object.assign(card, JSON.parse(this.editBackup));
      this.editingKey = null;
    },
    addCoverage() {
      const row = { key: this.nextKey++, isNew: true, active: true, practices: [], programs: [], stages: [], contracts: [], ruleIds: [], since: '', roleId: this.roleIdFor(this.selectedId) };
      if (!this.coverageByMember[this.selectedId]) this.coverageByMember[this.selectedId] = [];
      this.coverageByMember[this.selectedId].push(row);
      this.startEdit(row);
    },
    async saveCard(card) {
      const memberId = this.selectedId;
      const wanted = this.tupleKeys(card);
      const mine = this.rules.filter(r => r.member === memberId);
      const byKey = {};
      for (const r of mine) byKey[this.ruleKey(r)] = r;
      const toActivate = [], toDeactivate = [];
      let needCreate = false;
      for (const k of wanted) {
        const r = byKey[k];
        if (!r) needCreate = true;
        else if (!r.is_active) toActivate.push(r.id);
      }
      for (const id of card.ruleIds) {
        const r = mine.find(x => x.id === id);
        if (r && r.is_active && !wanted.has(this.ruleKey(r))) toDeactivate.push(r.id);
      }
      await this.run(memberId, 'Save coverage', async () => {
        let created = 0;
        if (needCreate) {
          const { data } = await axios.post('/api/team-assignments/bulk-assign/', this.cardPayload(memberId, card));
          created = data.assigned || 0;
        }
        const on = await this.setActive(toActivate, true);
        const off = await this.setActive(toDeactivate, false);
        this.editingKey = null;
        return `Coverage saved: ${created} rule rows written, ${on} restored, ${off} ended`;
      });
    },
    async endCard(card) {
      await this.run(this.selectedId, 'End coverage', async () => {
        const n = await this.setActive(card.ruleIds, false);
        return `Coverage ended: ${n} rule rows switched off`;
      });
    },
    async restoreCard(card) {
      await this.run(this.selectedId, 'Restore coverage', async () => {
        const n = await this.setActive(card.ruleIds, true);
        return `Coverage restored: ${n} rule rows switched on`;
      });
    },
    async setTakingPatients(memberId, on) {
      const ids = this.rules.filter(r => r.member === memberId && r.is_active !== on).map(r => r.id);
      await this.run(memberId, on ? 'Take new patients' : 'Stop new patients', async () => {
        const n = await this.setActive(ids, on);
        return `${on ? 'Taking' : 'Not taking'} new patients: ${n} rule rows switched ${on ? 'on' : 'off'}`;
      });
    },

    async openTasks(memberId, programs) {
      const data = await fetchPatientWorkQueue({
        start_row: 0, end_row: 999999, task_owner_id: memberId,
        filter_args: { metric_ids: [], show_completed: false, include_snoozed: true },
      });
      const programIds = new Set((programs || []).map(p => this.programIdByName[p]));
      const tasks = [];
      for (const p of data.rowData || []) {
        for (const t of p.tasks || []) {
          if (!ACTIVE_TASK_STATUSES.includes(t.status_id)) continue;
          if (!(t.owners || []).some(o => o.id === memberId)) continue;
          if (programIds.size && !programIds.has(t.metric_id)) continue;
          tasks.push({ id: t.id, opportunityId: t.opportunity_id, metricId: t.metric_id });
        }
      }
      return tasks;
    },
    async applyHandover() {
      const memberId = this.selectedId;
      const { to, programs, mode } = this.form;
      await this.run(memberId, 'Hand over', async () => {
        const tasks = await this.openTasks(memberId, programs);
        if (!tasks.length) return 'Hand over: no open tasks matched';
        const body = { member_id: to.id, task_ids: tasks.map(t => t.id) };
        if (mode === 'transfer') body.opportunity_ids = [...new Set(tasks.map(t => t.opportunityId).filter(Boolean))];
        const { data } = await axios.post('/api/team-assignments/bulk-assign/', body);
        this.form = null;
        await this.reloadCaseloads();
        return `${mode === 'transfer' ? 'Transferred' : 'Covered'} ${data.tasks ?? tasks.length} tasks${mode === 'transfer' ? ` and ${data.cases ?? body.opportunity_ids.length} cases` : ''} to ${to.name}`;
      });
    },
    async applyAway() {
      const memberId = this.selectedId;
      const name = this.selected.name;
      const backups = this.form.backups.map(b => ({ ...b, programs: [...b.programs] }));
      const moveTasks = this.form.moveTasks;
      const cards = this.activeCards(memberId);
      const offIds = this.rules.filter(r => r.member === memberId && r.is_active).map(r => r.id);
      await this.run(memberId, 'Mark away', async () => {
        const copied = {};
        for (const b of backups) {
          const before = new Set(this.rules.filter(r => r.member === b.memberId).map(r => r.id));
          for (const card of cards) {
            const programs = b.programs.length ? (card.programs.length ? card.programs.filter(p => b.programs.includes(p)) : b.programs) : card.programs;
            if (b.programs.length && card.programs.length && programs.length === 0) continue;
            await axios.post('/api/team-assignments/bulk-assign/', this.cardPayload(b.memberId, card, programs));
          }
          copied[b.memberId] = before;
        }
        await this.setActive(offIds, false);
        let moved = 0;
        if (moveTasks && backups.length) {
          const tasks = await this.openTasks(memberId, []);
          for (const b of backups) {
            const ids = tasks.filter(t => !b.programs.length || b.programs.map(p => this.programIdByName[p]).includes(t.metricId)).map(t => t.id);
            if (!ids.length) continue;
            await axios.post('/api/team-assignments/bulk-assign/', { member_id: b.memberId, task_ids: ids });
            moved += ids.length;
          }
        }
        await this.reloadRules();
        for (const b of backups) {
          b.copyIds = this.rules.filter(r => r.member === b.memberId && !copied[b.memberId].has(r.id)).map(r => r.id);
        }
        this.away[memberId] = { backups, offIds };
        this.form = null;
        await this.reloadCaseloads();
        return `${name} marked away: ${offIds.length} rule rows off, ${backups.length} backup${backups.length === 1 ? '' : 's'} given copies${moveTasks ? `, ${moved} tasks moved as cover` : ''}`;
      });
    },
    async comeBack(memberId) {
      const a = this.away[memberId];
      await this.run(memberId, 'Back now', async () => {
        const on = await this.setActive(a.offIds, true);
        let off = 0;
        for (const b of a.backups) off += await this.setActive(b.copyIds || [], false);
        delete this.away[memberId];
        return `Back: ${on} rule rows on, ${off} backup copies switched off. Covered tasks return to the owner at their next stage.`;
      });
    },

    deriveCoverage() {
      const groups = {};
      for (const r of this.rules) {
        const scope = `${r.metric || 0}|${r.task_type || 0}|${r.contract || 0}|${r.is_active ? 1 : 0}`;
        const g = groups[r.member] || (groups[r.member] = {});
        const slot = g[scope] || (g[scope] = { active: !!r.is_active, programs: new Set(), stages: new Set(), contracts: {}, practices: {}, every: false, since: '9999-12-31', ids: [], roleId: r.role });
        if (r.metric) slot.programs.add(r.metric_name);
        if (r.task_type) slot.stages.add(r.task_type_name);
        if (r.contract) slot.contracts[r.contract] = { id: r.contract, name: r.contract_name };
        if (r.entity) slot.practices[r.entity] = { id: r.entity, name: r.entity_name, type: r.entity_type_name }; else slot.every = true;
        slot.ids.push(r.id);
        const since = (r.created_at || '').slice(0, 10);
        if (since && since < slot.since) slot.since = since;
      }
      const out = {};
      for (const [memberId, scopes] of Object.entries(groups)) {
        const merged = {};
        for (const slot of Object.values(scopes)) {
          const key = `${slot.active ? 1 : 0}|${slot.every ? 'all' : Object.keys(slot.practices).sort().join(',')}|${Object.keys(slot.contracts).sort().join(',')}`;
          const m = merged[key] || (merged[key] = {
            key: this.nextKey++, active: slot.active, roleId: slot.roleId,
            practices: slot.every ? [] : Object.values(slot.practices).sort((a, b) => a.name.localeCompare(b.name)),
            programs: new Set(), stages: new Set(), contracts: {}, since: '9999-12-31', ruleIds: [],
          });
          slot.programs.forEach(p => m.programs.add(p));
          slot.stages.forEach(s => m.stages.add(s));
          Object.assign(m.contracts, slot.contracts);
          m.ruleIds.push(...slot.ids);
          if (slot.since < m.since) m.since = slot.since;
        }
        out[memberId] = Object.values(merged).map(m => ({
          ...m, programs: [...m.programs].sort(), stages: [...m.stages].sort(),
          contracts: Object.values(m.contracts).sort((a, b) => a.name.localeCompare(b.name)),
          since: m.since === '9999-12-31' ? '' : m.since,
        }));
      }
      return out;
    },
    derivePracticeRows() {
      const out = {};
      for (const r of this.rules) {
        if (!r.is_active) continue;
        const rows = out[r.entity || 0] || (out[r.entity || 0] = {});
        const row = rows[r.member] || (rows[r.member] = {
          key: this.nextKey++, person: { id: r.member, name: r.member_name, role: r.role_name },
          programs: new Set(), stages: new Set(), contracts: {}, since: '9999-12-31',
        });
        if (r.metric) row.programs.add(r.metric_name);
        if (r.task_type) row.stages.add(r.task_type_name);
        if (r.contract) row.contracts[r.contract] = { id: r.contract, name: r.contract_name };
        const since = (r.created_at || '').slice(0, 10);
        if (since && since < row.since) row.since = since;
      }
      for (const id of Object.keys(out)) {
        out[id] = Object.values(out[id]).map(row => ({
          ...row, programs: [...row.programs].sort(), stages: [...row.stages].sort(),
          contracts: Object.values(row.contracts).sort((a, b) => a.name.localeCompare(b.name)),
          since: row.since === '9999-12-31' ? '' : row.since,
        })).sort((a, b) => a.person.name.localeCompare(b.person.name));
      }
      return out;
    },

    async reloadRules() {
      const { data } = await axios.get('/api/team-assignments/', { params: { rules_only: 'true', metric_ids: this.metricIdsParam } });
      this.rules = data.filter(r => r.member);
      this.coverageByMember = this.deriveCoverage();
      this.practiceRows = this.derivePracticeRows();
    },
    async reloadCaseloads() {
      const { data } = await axios.get('/api/care-management/caseloads/', { params: { metric_ids: this.metricIdsParam } });
      const cl = {};
      for (const m of data.members || []) cl[m.member_id] = m;
      this.caseloads = cl;
    },
    async onTypeChange() {
      this.selectedId = null;
      this.search = '';
      await this.loadEntities();
      this.selectedId = this.entities[0]?.id || null;
    },
    async loadEntities() {
      this.entitiesLoading = true;
      const { data } = await axios.get('/api/entities/', { params: { entity_type_id: this.typeId, page_size: 1000 } });
      this.entities = (data.results || data || []).map(e => ({ id: e.id, name: e.name })).sort((a, b) => a.name.localeCompare(b.name));
      this.entitiesLoading = false;
    },
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const [programs, typesRes] = await Promise.all([listCareManagementPrograms(), axios.get('/api/entity-types/')]);
        const idToName = {};
        for (const p of programs) { idToName[p.id] = p.display_name || p.name; this.programIdByName[idToName[p.id]] = p.id; }
        this.programNames = Object.values(idToName).sort();
        const types = (typesRes.data.results || typesRes.data || []).filter(t => t.is_active !== false);
        const members = types.filter(t => MEMBER_TYPE_NAMES.includes(t.type_name));
        this.entityTypes = [...members, ...types.filter(t => !MEMBER_TYPE_NAMES.includes(t.type_name)).sort((a, b) => a.type_name.localeCompare(b.type_name))];
        this.typeId = (members[0] || this.entityTypes[0])?.id || null;
        const [stages] = await Promise.all([
          axios.get('/api/metrictasktypes/', { params: { metric_ids: this.metricIdsParam } }),
          this.loadEntities(),
          this.reloadCaseloads(),
        ]);
        const byProgram = {};
        for (const mtt of stages.data) {
          const program = idToName[mtt.metric];
          const stageName = mtt.task_type_details?.name || `Task type ${mtt.task_type}`;
          if (program) (byProgram[program] || (byProgram[program] = [])).push(stageName);
          this.stageIdByName[stageName] = mtt.task_type;
        }
        this.stagesByProgram = byProgram;
        await this.reloadRules();
        this.selectedId = this.entities[0]?.id || null;
      } catch (e) {
        this.error = e?.response?.data?.detail || e.message || 'Could not load.';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.cp { padding: 16px; background: #F8F8F9; min-height: 100%; font-size: 13px; color: #353537; }
.cp-cols { display: grid; grid-template-columns: 300px minmax(0, 1fr); gap: 12px; align-items: start; }
.cp-col { background: #fff; border: 1px solid #E6E6E8; border-radius: 6px; padding: 14px; display: flex; flex-direction: column; gap: 14px; min-width: 0; }
.cp-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.cp-head h2 { font-size: 16px; font-weight: 600; margin: 0; }
.cp-sub { font-size: 11.5px; color: #8A8A8C; font-weight: 400; }
.cp-switch { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #626264; cursor: pointer; }
.cp-switch.busy { opacity: 0.6; }
.cp-banner { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; border-radius: 6px; background: #FFF4EF; border: 1px solid #F9C6B0; font-size: 12.5px; }
.cp-banner.bad { background: #FEF2F2; border-color: #FCA5A5; color: #991B1B; }
.cp-block { display: flex; flex-direction: column; gap: 10px; }
.cp-block-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.cp-block-head h3 { margin: 0; font-size: 13.5px; font-weight: 600; }
.cp-block-actions { display: flex; align-items: center; gap: 6px; }
.cp-toggle { font-size: 11.5px; color: #626264; display: flex; align-items: center; gap: 4px; cursor: pointer; margin-right: 6px; }
.cp-table { width: 100%; }
.cp-table tr.cp-total td { font-weight: 600; border-top: 1px solid #E6E6E8; }
.cp-role { font-size: 11px; color: #8A8A8C; font-weight: 400; }
.cp-rows { list-style: none; margin: 0; padding: 0; max-height: 70vh; overflow: auto; }
.cp-rows li { padding: 7px 10px; border-left: 3px solid transparent; border-bottom: 1px solid #F3F4F6; cursor: pointer; }
.cp-rows li:hover { background: #FAFAFA; }
.cp-rows li.on { border-left-color: #F76734; background: #FFF4EF; }
.cp-rows li.bare .cp-row-sub { color: #D7402B; }
.cp-row-top { display: flex; justify-content: space-between; gap: 8px; }
.cp-row-name { font-weight: 600; }
.cp-row-count { font-size: 12px; color: #626264; }
.cp-row-sub { font-size: 11.5px; color: #8A8A8C; }
.cp-away-dot { color: #B45309; }
.cp-none { font-size: 12px; color: #8A8A8C; padding: 6px 0; cursor: default; margin: 0; }
.cp-foot { font-size: 11.5px; color: #8A8A8C; padding-top: 8px; border-top: 1px solid #F3F4F6; }
.cp-form { border: 1px solid #E6E6E8; border-radius: 6px; padding: 12px; display: flex; flex-direction: column; gap: 12px; background: #FCFCFD; }
.cp-form h4 { margin: 0; font-size: 13px; font-weight: 600; }
.cp-form-field { display: flex; flex-direction: column; gap: 6px; }
.cp-form-field > span { font-size: 12px; font-weight: 600; color: #626264; }
.cp-form-foot { display: flex; justify-content: flex-end; gap: 6px; }
.cp-radio { display: flex; align-items: center; gap: 6px; font-size: 12.5px; }
.cp-backup { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; }
.cp-hint { margin: 0; font-size: 11.5px; color: #8A8A8C; }
.cp-card { border: 1px solid #E6E6E8; border-radius: 6px; }
.cp-card.ended { opacity: 0.6; }
.cp-card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; padding: 10px 14px; border-bottom: 1px solid #F3F4F6; }
.cp-card-body { display: flex; flex-direction: column; gap: 14px; padding: 12px 14px; }
.cp-fields { display: flex; flex-direction: column; gap: 12px; padding: 12px 14px; }
.cp-facet h5 { margin: 0 0 6px; font-size: 12px; font-weight: 600; color: #626264; }
.cp-group { margin-bottom: 8px; }
.cp-group-title { font-size: 11.5px; color: #8A8A8C; margin-bottom: 4px; }
.cp-chips { display: flex; flex-wrap: wrap; gap: 4px; }
.cp-tag { font-size: 12px; padding: 2px 8px; border-radius: 4px; background: #F3F4F6; color: #353537; }
.cp-chip { display: inline-flex; align-items: center; gap: 2px; font-size: 12px; padding: 3px 4px 3px 10px; border-radius: 999px; border: 1px solid #F76734; background: #FFF4EF; color: #C2410C; }
.cp-chip.person { border-color: #7799C2; background: #EEF3FA; color: #2F4F7F; padding-right: 10px; }
.cp-all { font-size: 12px; color: #8A8A8C; margin: 0; }
.cp-more { font-size: 11.5px; padding: 2px 6px; border: 0; background: transparent; color: #C2410C; cursor: pointer; }
.cp-programs { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.cp-program { display: block; font-weight: 600; }
.cp-stages { display: block; font-size: 11.5px; color: #626264; }
.cp-x { border: 0; background: transparent; color: #A5A5A6; font-size: 15px; line-height: 1; cursor: pointer; padding: 0 4px; }
.cp-x:hover { color: #D7402B; }
.cp-picker { list-style: none; margin: 0; padding: 0; border: 1px solid #E6E6E8; border-radius: 6px; max-height: 180px; overflow: auto; background: #fff; }
.cp-picker li { padding: 6px 10px; cursor: pointer; display: flex; justify-content: space-between; gap: 8px; }
.cp-picker li:hover { background: #FAFAFA; }
.cp-picker em { font-style: normal; font-size: 11px; color: #8A8A8C; }
.cp-history { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; font-size: 12.5px; }
.cp-history .cm-tnum { color: #8A8A8C; margin-right: 8px; }
.cp-blank { color: #8A8A8C; align-items: center; justify-content: center; min-height: 200px; }
@media (max-width: 900px) { .cp-cols { grid-template-columns: 1fr; } }
</style>
